import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { UserCheck, Loader2, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';

/**
 * Asignación de patrón a empleado (vista Empleado).
 */
export function LaborEmployeePatternSetup({
  employeeId,
  employeeName,
  factoryId,
  year,
  calendarId,
  patterns = [],
  loadingPatterns = false,
  onAfterChange,
}) {
  const [assignPatternId, setAssignPatternId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setStartDate(`${year}-01-01`);
    setEndDate('');
    setAssignPatternId('');
    setMessage(null);
  }, [employeeId, year]);

  const mutation = useMutation({
    mutationFn: async () => {
      const patId = Number(assignPatternId);
      const calId = Number(calendarId);
      if (!patId) throw new Error('Selecciona un patrón de trabajo.');
      if (!calId) throw new Error('No hay calendario de planta para este año. Créalo en la vista Fábrica.');
      if (!startDate) throw new Error('Indica la fecha de inicio de la asignación.');

      return laborScheduleService.assignPatternToEmployee(Number(employeeId), {
        patternId: patId,
        calendarId: calId,
        startDate,
        endDate: endDate || null,
      });
    },
    onSuccess: (result) => {
      const pat = patterns.find((p) => String(p.id) === String(assignPatternId));
      const assignId = result?.id ? ` (asignación ID ${result.id})` : '';
      setMessage({
        type: 'success',
        text: `Patrón «${pat?.name || assignPatternId}» asignado a ${employeeName}${assignId}.`,
      });
      onAfterChange?.();
    },
    onError: (err) => {
      setMessage({
        type: 'error',
        text: err?.message || 'No se pudo asignar el patrón al empleado.',
      });
    },
  });

  const canAssign =
    Boolean(employeeId) &&
    Boolean(factoryId) &&
    Boolean(calendarId) &&
    Boolean(assignPatternId) &&
    Boolean(startDate) &&
    patterns.length > 0;

  return (
    <div className="mb-4 rounded-lg border border-gray-700 bg-gray-800/80 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="text-sm text-gray-400 mr-1 flex items-center gap-1.5 w-full sm:w-auto">
          <UserCheck className="w-4 h-4 shrink-0" />
          Asignar patrón a {employeeName || `empleado ${employeeId}`}:
        </span>

        <div className="min-w-[180px] flex-1 max-w-xs">
          <label htmlFor="labor-assign-pattern" className="block text-xs text-gray-400 mb-1">
            Patrón
          </label>
          <select
            id="labor-assign-pattern"
            value={assignPatternId}
            onChange={(e) => {
              setAssignPatternId(e.target.value);
              setMessage(null);
            }}
            disabled={loadingPatterns || patterns.length === 0}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="">
              {loadingPatterns
                ? 'Cargando patrones…'
                : patterns.length === 0
                  ? 'Sin patrones (crea uno en vista Patrón)'
                  : 'Seleccionar patrón…'}
            </option>
            {patterns.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="labor-assign-start" className="block text-xs text-gray-400 mb-1">
            Inicio
          </label>
          <input
            id="labor-assign-start"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setMessage(null);
            }}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label htmlFor="labor-assign-end" className="block text-xs text-gray-400 mb-1">
            Fin (opcional)
          </label>
          <input
            id="labor-assign-end"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setMessage(null);
            }}
            min={startDate || undefined}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>

        <button
          type="button"
          onClick={() => {
            setMessage(null);
            mutation.mutate();
          }}
          disabled={!canAssign || mutation.isPending}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium disabled:opacity-50"
        >
          {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Asignar patrón
        </button>
      </div>

      {!factoryId && (
        <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          Selecciona una fábrica en el header para cargar patrones y calendario.
        </p>
      )}

      {factoryId && !calendarId && !loadingPatterns && (
        <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          No hay calendario de planta para {year}. Créalo en la vista Fábrica.
        </p>
      )}

      {message?.type === 'success' && (
        <p className="mt-2 text-sm text-emerald-400">{message.text}</p>
      )}
      {message?.type === 'error' && (
        <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {message.text}
        </p>
      )}
    </div>
  );
}
